// talkingAvatarVoice — the voices the talking-avatar video is converted to.
//
// Two providers (voice-providers endpoint; a voice without `provider` — every
// record from before Azure existed — is ElevenLabs):
//   • ElevenLabs: stock voices from api/course/voices — there `id` IS the
//     provider id; the org's cloned voices from api/tts-avatar/voices — DB `id`
//     + `providerId`.
//   • Azure (Beta, Microsoft public preview): the multilingual conversion voices
//     listed by api/talking-avatar/voice-providers (`voices`) — `id` is the SSML
//     voice name (e.g. en-US-AvaMultilingualNeural) and is what the job is
//     started with. Audio-driven models (OmniHuman) convert nothing, so they
//     also take every other Azure TTS voice (`ttsVoices`, e.g. tr-TR-EmelNeural).
// The job is always started with `providerId` plus voiceProvider = `provider`.
// Normalized: { id, providerId, name, gender, isPrivate, provider,
//               sampleUrlsByLanguage, sampleUrl }
import { pickSampleLanguage } from "../../../utils/sampleVoiceLanguage";
import { tt } from "./talkingAvatarI18n";

export const VOICE_PROVIDER_ELEVENLABS = "elevenlabs";
export const VOICE_PROVIDER_AZURE = "azure";

const sampleOf = (v) => v.sampleUrl || v.audioSampleUrl || v.previewUrl || null;

export function voiceFromStock(v) {
  if (!v) return null;
  return {
    id: v.id,
    providerId: v.providerId || v.id,
    name: v.name || String(v.id || ""),
    gender: v.gender || "",
    isPrivate: false,
    provider: VOICE_PROVIDER_ELEVENLABS,
    sampleUrlsByLanguage: v.sampleUrlsByLanguage || {},
    sampleUrl: sampleOf(v),
  };
}

export function voiceFromPrivate(v) {
  if (!v) return null;
  return {
    id: v.id,
    providerId: v.providerId || null,
    name: v.name || String(v.providerId || v.id || ""),
    gender: v.gender || "",
    isPrivate: true,
    provider: VOICE_PROVIDER_ELEVENLABS,
    sampleUrlsByLanguage: v.sampleUrlsByLanguage || {},
    sampleUrl: sampleOf(v),
  };
}

export function voiceFromAzure(v) {
  if (!v || !v.id) return null;
  return {
    id: v.id,
    providerId: v.id,
    name: v.name || String(v.id),
    gender: v.gender || "",
    isPrivate: false,
    provider: VOICE_PROVIDER_AZURE,
    sampleUrlsByLanguage: v.sampleUrlsByLanguage || {},
    sampleUrl: sampleOf(v),
  };
}

const str = (v) => (v == null ? "" : String(v));

// The raw Azure voices the picker offers for a model: the voice-conversion
// voices, plus — for an audio-driven model — every other Azure TTS voice.
export function azurePickerVoices(provider, audioDriven) {
  const conversion = provider && Array.isArray(provider.voices) ? provider.voices : [];
  if (!audioDriven || !provider || !Array.isArray(provider.ttsVoices)) return conversion;
  const seen = new Set(conversion.map((v) => str(v && v.id).toLowerCase()));
  const extra = provider.ttsVoices.filter((v) => {
    const key = str(v && v.id).toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...conversion, ...extra];
}

// Every name a saved narration / voice profile uses for a provider.
const providerOf = (p) => {
  const v = str(p).trim().toLowerCase();
  if (v === "elevenlabs" || v === "1") return "elevenlabs";
  if (v === "private" || v === "cloned" || v === "clone") return "private";
  if (v === "azure" || v === "microsoft" || v === "0") return "azure";
  return v;
};

export const voiceProviderOf = (voice) =>
  voice && providerOf(voice.provider) === "azure"
    ? VOICE_PROVIDER_AZURE
    : VOICE_PROVIDER_ELEVENLABS;

export const voiceProviderLabel = (provider) =>
  providerOf(provider) === "azure"
    ? tt("TalkingAvatar.Voice.ProviderAzure", "Azure")
    : tt("TalkingAvatar.Voice.ProviderElevenLabs", "ElevenLabs");

// "Ava · Azure" — the voice as the job view / records show it.
export function voiceDisplayLabel(voice) {
  const name = voice && str(voice.name).trim();
  return name ? `${name} · ${voiceProviderLabel(voice.provider)}` : "";
}

export function sameVoice(a, b) {
  if (!a || !b) return false;
  if (a.provider && b.provider && voiceProviderOf(a) !== voiceProviderOf(b)) return false;
  const keysA = [str(a.id), str(a.providerId)].filter(Boolean);
  const keysB = [str(b.id), str(b.providerId)].filter(Boolean);
  return keysA.some((k) => keysB.includes(k));
}

// The Azure list carries no samples; the AI voice tab's own Azure list
// (api/course/voices) does — merged by voice id, case-insensitively.
export function attachVoiceSamples(voices, sampleVoices) {
  const list = Array.isArray(voices) ? voices : [];
  if (!Array.isArray(sampleVoices) || !sampleVoices.length) return list;
  const byId = new Map();
  sampleVoices.forEach((s) => {
    if (s && s.id) byId.set(str(s.id).toLowerCase(), s);
  });
  return list.map((v) => {
    const s = v && byId.get(str(v.id).toLowerCase());
    if (!s) return v;
    const byLang = s.sampleUrlsByLanguage;
    return {
      ...v,
      sampleUrlsByLanguage:
        byLang && typeof byLang === "object" && Object.keys(byLang).length
          ? byLang
          : v.sampleUrlsByLanguage,
      sampleUrl: sampleOf(s) || v.sampleUrl,
    };
  });
}

function findOrgVoice(orgVoices, { id, name }) {
  const list = Array.isArray(orgVoices) ? orgVoices : [];
  const key = str(id).trim();
  const byId = key ? list.find((v) => str(v.id) === key || str(v.providerId) === key) : null;
  if (byId) return voiceFromPrivate(byId);
  const wanted = str(name).trim().toLowerCase();
  const byName = wanted ? list.find((v) => str(v.name).trim().toLowerCase() === wanted) : null;
  return byName ? voiceFromPrivate(byName) : null;
}

function findAzureVoice(azureVoices, id) {
  const key = str(id).trim().toLowerCase();
  if (!key) return null;
  const list = Array.isArray(azureVoices) ? azureVoices : [];
  return voiceFromAzure(list.find((v) => v && str(v.id).toLowerCase() === key));
}

// Which voice to preselect for a scene:
//   the scene narration's own voice when it is an Azure voice that can convert
//   (in `azureVoices` — pass none while Azure is unusable) → that Azure voice;
//   when it is ElevenLabs (stock → that voice, own/private → the org voice
//   matched by id, providerId or name); else the training's voice profile when
//   it is ElevenLabs; else none.
const normGender = (g) =>
  String(g || "")
    .trim()
    .toLowerCase();

/** The voice's known gender differs from the avatar's known gender (unknown → no mismatch). */
export function voiceGenderMismatch(voice, avatarGender) {
  const v = normGender(voice && voice.gender);
  const a = normGender(avatarGender);
  return !!v && !!a && v !== a;
}

export function resolveInitialVoice({
  soundVoiceData,
  voiceProfile,
  orgVoices,
  azureVoices,
  avatarGender,
} = {}) {
  // A preselected voice of the other gender (a male avatar over a scene
  // narrated by a female voice) is never picked silently.
  const fits = (v) => (v && !voiceGenderMismatch(v, avatarGender) ? v : null);
  const fromScene = fits(sceneVoice(soundVoiceData, orgVoices, azureVoices));
  if (fromScene) return fromScene;
  return fits(profileVoice(voiceProfile, orgVoices));
}

function sceneVoice(soundVoiceData, orgVoices, azureVoices) {
  const vd = soundVoiceData || null;
  if (vd) {
    const provider = providerOf(vd.provider);
    if (provider === "azure" && vd.narratorMode !== "own") {
      const azure = findAzureVoice(azureVoices, vd.voiceId);
      if (azure) return azure;
    } else if (provider === "private" || vd.narratorMode === "own") {
      return findOrgVoice(orgVoices, { id: vd.voiceId, name: vd.voiceName });
    } else if (provider === "elevenlabs" && vd.voiceId) {
      return voiceFromStock({
        id: vd.voiceId,
        name: vd.voiceName || vd.voiceId,
        gender: vd.gender,
      });
    }
  }
  return null;
}

function profileVoice(voiceProfile, orgVoices) {
  if (voiceProfile) {
    const provider = providerOf(voiceProfile.voiceServiceProvider);
    const detail = voiceProfile.voiceDetail || {};
    const id = detail.id || voiceProfile.voiceName;
    const name = detail.name || voiceProfile.voiceName;
    if (provider === "private") return findOrgVoice(orgVoices, { id, name });
    if (provider === "elevenlabs" && id) {
      return voiceFromStock({ id, name: name || id, gender: detail.gender });
    }
  }
  return null;
}

export function pickVoiceSampleUrl(voice) {
  if (!voice) return null;
  const byLang = voice.sampleUrlsByLanguage || {};
  const langs = Object.keys(byLang);
  if (langs.length) {
    const lang = pickSampleLanguage(langs);
    const entry = lang && byLang[lang];
    const url = typeof entry === "string" ? entry : entry && (entry.v1 || entry.v2);
    if (url) return url;
  }
  return voice.sampleUrl || voice.audioSampleUrl || voice.previewUrl || null;
}
